"use strict";
/**
 * The single, canonical source of truth for a `PharmacySale`'s real
 * lifecycle state — DRAFT / POSTED / CANCELLED, computed at read time
 * from fields that already exist, never a stored status enum. Shared
 * between backend and frontend via this package specifically so there
 * is exactly one implementation, not two hand-synced copies.
 *
 * Why computed, not stored: `PharmacySale.status` (`PharmacySaleStatus`
 * — COMPLETED/CANCELLED) is set to COMPLETED the moment `createSale()`
 * runs, well before the sale is ever actually billed — a brand new,
 * never-touched draft and a fully posted, GST-invoiced sale both carry
 * `status: 'COMPLETED'` today. The one fact that can't lie about
 * whether a sale was really posted is whether a real accounting/invoice
 * side-effect exists for it: `invoiceId` (OP/IP, set by
 * `billOpSale()`/`billIpSale()`) or `sourceEventLogId` (Walk-in, set by
 * `billWalkInSale()`'s `postingApplicationService.recordEvent()` call).
 * Neither field is EVER set before real billing, and both are refused
 * to be re-held once set (`holdSale()`'s own guard) — this is real
 * mechanical proof, not an inferred convention like sale-number prefix
 * matching (`TEMP-`), which only catches drafts created after that
 * numbering scheme was introduced and silently misses older ones that
 * happened to get a real sequential number at creation time (found live
 * via a production sale's own audit trail: `PHS2026000167`, real number,
 * `heldAt` set, `invoiceId`/`sourceEventLogId` both null — never billed).
 *
 * `VOID`/`RETURNED` are NOT real states this codebase can currently
 * produce — there is no "void a posted sale" action anywhere, and a
 * Return is tracked per-LINE, not as a whole-sale state. `isReturned()`/
 * `isPartiallyReturned()` are exposed anyway (taking lines as a second
 * argument) for a consumer that already has line-level return-quantity
 * data loaded and wants to show a returned indicator — every other
 * consumer here only has the sale header, and loading every row's lines
 * just to check this would be a real N+1 query cost for no
 * currently-real benefit.
 *
 * Return-visibility fix (partial-return awareness): these two functions
 * used to read only `PharmacySaleLine.returnedAt` (a boolean-ish column
 * only ever set once a line is returned in FULL). That's wrong once a
 * line can be returned partially and multiple times — a line with some,
 * but not all, of its quantity returned left both functions blind.
 * `returnedQuantity` per line (summed from `StockReturnEntry` via
 * `ReturnCalculationService`, never cached) is now the required input;
 * `returnedAt` itself is not read here at all.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SaleLifecycle = void 0;
function isPosted(sale) {
    return sale.invoiceId != null || sale.sourceEventLogId != null;
}
function isCancelled(sale) {
    return !isPosted(sale) && sale.status === 'CANCELLED';
}
function isDraft(sale) {
    return !isPosted(sale) && !isCancelled(sale);
}
/** A draft that's currently parked (Hold Bills) rather than actively open in a billing grid. Only meaningful when `isDraft()` is also true. */
function isHeld(sale) {
    return isDraft(sale) && sale.heldAt != null;
}
function state(sale) {
    if (isPosted(sale))
        return 'POSTED';
    if (isCancelled(sale))
        return 'CANCELLED';
    return 'DRAFT';
}
/** Not a real system state today (see file header) — exposed so a consumer with line data can show a "Returned" indicator layered on top of `state()`, never in place of it. Every line's returned quantity must have reached its full original quantity. */
function isReturned(lines) {
    return lines.length > 0 && lines.every((line) => line.returnedQuantity >= line.quantity);
}
/** True when SOME return activity exists but not every line has been fully returned yet — covers a single line with a partial return, and a mix of fully-returned and untouched lines alike. */
function isPartiallyReturned(lines) {
    return lines.some((line) => line.returnedQuantity > 0) && !isReturned(lines);
}
exports.SaleLifecycle = {
    isPosted,
    isCancelled,
    isDraft,
    isHeld,
    isReturned,
    isPartiallyReturned,
    state,
    /** A held draft can be reopened into the billing grid. */
    canResume: (sale) => isHeld(sale),
    /** Lines can be added/removed/changed — quantity, batch, discount, doctor, patient — as long as nothing has actually been posted yet. */
    canEdit: (sale) => isDraft(sale),
    /** Can run through billOpSale/billIpSale/billWalkInSale. */
    canBill: (sale) => isDraft(sale),
    /** Can be parked via holdSale — only a not-yet-held draft. */
    canHold: (sale) => isDraft(sale) && sale.heldAt == null,
    /** Can be cleared via cancelHeldSale — only a currently-held draft (an actively-open, never-held draft has nothing server-side to cancel yet). */
    canCancel: (sale) => isHeld(sale),
};
//# sourceMappingURL=pharmacy-sale-lifecycle.js.map